import React, { useCallback, useEffect, useRef, useState } from 'react';
import ConfirmDialog from '../AlmeDialog/ConfirmDialog';
import AlmeOverlaySurveyDialog from '../AlmeOverlayForm/OverlaySurvey/AlmeOverlaySurveyDialog';
import { getSurveyUrl } from '../../utils/UrlUtils';
import { getAlmeSessionData } from '../AlmeApp/AlmeAppSessionState';
import { focusMessageBox } from '../AlmeUtilities/dom-utils';
import { getCurrentHadLiveChatCookieValue } from '../LiveChat/AlmeLiveChatSurvey/HadLiveChatQualtricsCookie';
export function renderSessionTimeoutDialog(almeState, appConfig, isLiveChatSurveyLoaded, onUserSessionRefresh, onSessionTimeout, onCloseClick) {
    return ((!almeState.sessionExpired && almeState.lastActivityTime && !almeState.isLiveChatConnected)
        ?
            React.createElement(IvaSessionTimeout, { lastActivityTime: almeState.lastActivityTime, almeAppConfig: appConfig, sessionTimeoutInMs: appConfig.sessionTimeoutInMs, sessionTimeoutWarningInMs: appConfig.sessionTimeoutInMs - appConfig.sessionTimeoutWarningPeriodInMs, onUserSessionRefresh: onUserSessionRefresh, onSessionTimeout: onSessionTimeout, onUserSessionTimeout: onCloseClick, isLoggedIn: almeState.isLoggedIn, chatId: almeState.chatId, isLivechatSurveyLoaded: isLiveChatSurveyLoaded })
        : null);
}
const useUpdateSessionTimeoutDialogState = (props, dialogState, isSurveyOpen, setDialogState, onSystemSessionTimeout, setFocusElement) => {
    useEffect(() => {
        const currentTime = Date.now();
        //Stop timer on open survey
        const timelapse = isSurveyOpen ? 0 : (currentTime - props.lastActivityTime);
        const sessionTimeoutWarningInMs = props.sessionTimeoutWarningInMs;
        const sessionTimeoutPeriodInMs = props.sessionTimeoutInMs;
        const warningPeriod = sessionTimeoutPeriodInMs - sessionTimeoutWarningInMs;
        const timeoutRemainder = sessionTimeoutPeriodInMs - timelapse;
        const warningRemainder = sessionTimeoutWarningInMs - timelapse;
        let timerId;
        if (dialogState.show) {
            if (timeoutRemainder <= 0) {
                onSystemSessionTimeout();
            }
            else if (warningRemainder <= 0) {
                timerId = window.setTimeout(() => setDialogState({ show: true, counter: Math.round(timeoutRemainder / 1000) }), 1000);
            }
            else {
                timerId = window.setTimeout(() => setDialogState({ show: true, counter: Math.round(warningPeriod / 1000) }), warningRemainder);
                setDialogState(Object.assign(Object.assign({}, dialogState), { show: false }));
            }
        }
        else {
            if (timeoutRemainder <= 0) {
                onSystemSessionTimeout();
            }
            else if (warningRemainder <= 0) {
                //Set return to focus element on opening dialog
                setFocusElement();
                setDialogState({ show: true, counter: Math.round(timeoutRemainder / 1000) });
            }
            else {
                timerId = window.setTimeout(() => {
                    setFocusElement();
                    setDialogState({ show: true, counter: Math.round(warningPeriod / 1000) });
                }, warningRemainder);
            }
        }
        return () => {
            if (timerId) {
                clearTimeout(timerId);
            }
        };
    }, [dialogState, isSurveyOpen, onSystemSessionTimeout, props.lastActivityTime, props.sessionTimeoutInMs, props.sessionTimeoutWarningInMs, setDialogState, setFocusElement]);
};
const IvaSessionTimeout = (props) => {
    var _a;
    const [dialogState, setDialogState] = useState({ show: false, counter: 0 });
    const [openSurvey, setOpenSurvey] = useState(false);
    const dialogRef = useRef(null); // Reference to the dialog container
    const surveyUrl = props.almeAppConfig["IvaOnly.SurveyUrl"];
    const finalSurveyUrl = getSurveyUrl(props.almeAppConfig, false, surveyUrl, props.isLoggedIn, props.chatId, (_a = getAlmeSessionData()) === null || _a === void 0 ? void 0 : _a.sessionId);
    const returnFocusToElement = useRef(null);
    const setFocusElement = useCallback(() => {
        returnFocusToElement.current = document.activeElement;
    }, []);
    const trapFocus = useCallback((e) => {
        if (dialogRef.current) {
            const focusableElements = dialogRef.current.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
            const firstElement = focusableElements[0];
            const lastElement = focusableElements[focusableElements.length - 1];
            if (e.key === 'Tab') {
                if (e.shiftKey && document.activeElement === firstElement) {
                    e.preventDefault();
                    lastElement.focus();
                }
                else if (!e.shiftKey && document.activeElement === lastElement) {
                    e.preventDefault();
                    firstElement.focus();
                }
            }
        }
    }, []);
    const handleSurveyClose = () => {
        setOpenSurvey(false);
        props.onUserSessionTimeout();
    };
    const onYesClick = () => {
        props.onUserSessionRefresh();
        focusMessageBox();
    };
    const onSystemSessionTimeout = useCallback(() => {
        props.onSessionTimeout();
    }, [props]);
    const onNoClick = useCallback(() => {
        if (props.chatId && !props.isLivechatSurveyLoaded && (getCurrentHadLiveChatCookieValue() === "false")) {
            // eslint-disable-next-line react-hooks/exhaustive-deps
            if (finalSurveyUrl !== "empty" && finalSurveyUrl !== '') {
                setOpenSurvey(true);
            }
            else {
                setOpenSurvey(false);
            }
        }
        else {
            props.onUserSessionTimeout();
        }
    }, [props, finalSurveyUrl]);
    useEffect(() => {
        var _a;
        if (dialogState.show) {
            // Set focus to the dialog when it opens
            const firstFocusableElement = (_a = dialogRef.current) === null || _a === void 0 ? void 0 : _a.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
            firstFocusableElement === null || firstFocusableElement === void 0 ? void 0 : firstFocusableElement.focus();
            // Add event listener for trapping focus
            document.addEventListener('keydown', trapFocus);
        }
        return () => {
            var _a;
            // Remove focus trap and restore focus when the dialog closes
            document.removeEventListener('keydown', trapFocus);
            (_a = returnFocusToElement.current) === null || _a === void 0 ? void 0 : _a.focus();
        };
    }, [dialogState.show, trapFocus]);
    useUpdateSessionTimeoutDialogState(props, dialogState, openSurvey, setDialogState, onSystemSessionTimeout, setFocusElement);
    return (React.createElement(React.Fragment, null,
        dialogState.show && (React.createElement("div", { ref: dialogRef, role: "dialog", "aria-labelledby": "alme-session-timeout-dialog-title", "aria-describedby": "alme-session-timeout-dialog-description", "aria-modal": "true" },
            React.createElement(ConfirmDialog, { id: "alme-session-timeout-dialog", ariaDescribedBy: "alme-session-timeout-dialog-description", role: "dialog", isOpen: dialogState.show, yesLabel: "Yes", noLabel: "No", onYes: onYesClick, onNo: onNoClick, ariaLabel: "Session Timeout", title: "", description: `Your chat session will expire in ${dialogState.counter} seconds. Do you wish to continue?`, height: 250 }))),
        openSurvey
            ? React.createElement(AlmeOverlaySurveyDialog, { isOpen: openSurvey, surveyURL: finalSurveyUrl, onCloseSurvey: handleSurveyClose })
            : null));
};
export default IvaSessionTimeout;
